import React, { useState, useEffect, useRef } from 'react';
import { SurveyData, User, DynamicQuestion, SurveySection, UserRole } from '../types';
import { Plus, Trash2, Save, User as UserIcon, ArrowRight, ArrowLeft, Loader, Camera, Check } from 'lucide-react';
import { saveSurvey, getTemplate, getDynamicQuestions, getNextSerialNo, getDynamicSections } from '../services/storage';
import { useToast } from './Toast';

interface SurveyFormProps {
  user: User;
  onSuccess: () => void;
  initialData?: SurveyData | null;
}

// Image Compression Helper
const compressImageForDb = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const MAX_DIM = 800;
        let width = img.width;
        let height = img.height;
        if (width > height) {
            if (width > MAX_DIM) {
                height *= MAX_DIM / width;
                width = MAX_DIM;
            }
        } else {
            if (height > MAX_DIM) {
                width *= MAX_DIM / height;
                height = MAX_DIM;
            }
        }
        canvas.width = width;
        canvas.height = height;
        if(!ctx) { reject(new Error("Canvas failed")); return; }
        ctx.drawImage(img, 0, 0, width, height);
        let quality = 0.7;
        let dataUrl = canvas.toDataURL('image/jpeg', quality);
        while (dataUrl.length > 270000 && quality > 0.2) {
            quality -= 0.1;
            dataUrl = canvas.toDataURL('image/jpeg', quality);
        }
        resolve(dataUrl);
      };
      img.onerror = (e) => reject(e);
    };
    reader.onerror = (e) => reject(e);
  });
};

// Simplified Question Card (Always Required)
const QuestionCard: React.FC<{ label: string, required?: boolean, children: React.ReactNode }> = ({ label, children }) => (
  <div className="bg-white rounded-lg p-5 border border-gray-200 mb-4">
    <label className="block text-sm font-semibold text-gray-700 mb-2 w-full uppercase tracking-wide">
      {label} <span className="text-red-500">*</span>
    </label>
    <div className="w-full block">{children}</div>
  </div>
);

const ImageInput = ({ value, onChange, onProcessingChange }: { value: string | undefined, onChange: (val: string) => void, onProcessingChange: (status: boolean) => void }) => {
  const [compressing, setCompressing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setCompressing(true);
      onProcessingChange(true);
      try {
        const compressedBase64 = await compressImageForDb(file);
        onChange(compressedBase64);
      } catch (err) {
        alert("Error processing image.");
      } finally {
        setCompressing(false);
        onProcessingChange(false);
      }
    }
  };

  const clearImage = () => {
    onChange('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="w-full">
       <input type="file" accept="image/*" capture="environment" className="hidden" ref={fileInputRef} onChange={handleFileChange} />
       {!value && (
         <button 
            type="button" 
            onClick={() => fileInputRef.current?.click()}
            disabled={compressing}
            className="w-full h-32 border border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-500 hover:bg-gray-50 hover:border-emerald-500 hover:text-emerald-600 transition-colors"
         >
            {compressing ? <Loader className="animate-spin mb-2" /> : <Camera size={32} className="mb-2" />}
            <span className="font-medium text-sm">{compressing ? 'Optimizing...' : 'Tap to Upload Photo'}</span>
         </button>
       )}
       {value && (
         <div className="relative w-full bg-gray-50 rounded-lg overflow-hidden border border-gray-200 p-2">
            <img src={value} alt="Uploaded" className="max-h-[300px] w-auto mx-auto rounded shadow-sm" />
            <div className="flex justify-between items-center mt-3 px-1">
               <span className="text-xs text-emerald-600 font-bold flex items-center"><Check size={12} className="mr-1"/> Saved</span>
               <button type="button" onClick={clearImage} className="text-xs text-red-500 hover:underline">Remove</button>
            </div>
         </div>
       )}
    </div>
  );
};

const YesNoInput = ({ value, onChange }: { value: boolean | string | undefined, onChange: (val: any) => void }) => {
  const isYes = value === true || value === 'Yes' || value === 'true';
  const isNo = value === false || value === 'No' || value === 'false';
  return (
    <div className="flex gap-3 w-full">
      <button type="button" onClick={() => onChange(true)} className={`flex-1 py-2.5 px-4 rounded border transition-all ${isYes ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
        <span className="font-bold text-sm">Yes / ഉണ്ട്</span>
      </button>
      <button type="button" onClick={() => onChange(false)} className={`flex-1 py-2.5 px-4 rounded border transition-all ${isNo ? 'bg-red-500 text-white border-red-500' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
        <span className="font-bold text-sm">No / ഇല്ല</span>
      </button>
    </div>
  );
};

const MultiSelect = ({ options, value, onChange }: { options: string[], value: string[], onChange: (v: string[]) => void }) => {
  const selected = value || [];
  const toggle = (opt: string) => {
    if (selected.includes(opt)) onChange(selected.filter(s => s !== opt));
    else onChange([...selected, opt]);
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      {options.map(opt => (
        <label key={opt} className={`flex items-center space-x-3 p-3 rounded cursor-pointer border transition-all ${selected.includes(opt) ? 'bg-emerald-50 border-emerald-500' : 'bg-white border-gray-200 hover:border-emerald-300'}`}>
           <input type="checkbox" className="w-4 h-4 accent-emerald-600" checked={selected.includes(opt)} onChange={() => toggle(opt)} />
           <span className="text-gray-800 text-sm">{opt}</span>
        </label>
      ))}
    </div>
  )
}

const InputClass = "w-full border border-gray-300 p-3 rounded bg-white text-gray-900 focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 outline-none block text-sm transition-all";

const SurveyForm: React.FC<SurveyFormProps> = ({ user, onSuccess, initialData }) => {
  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [questions, setQuestions] = useState<DynamicQuestion[]>([]);
  const [sections, setSections] = useState<SurveySection[]>([]);
  const { addToast } = useToast();
  const [isMediaProcessing, setIsMediaProcessing] = useState(false);
  
  const [formData, setFormData] = useState<Record<string, any>>({});

  useEffect(() => {
    const init = async () => {
      try {
        const [qs, secs] = await Promise.all([getDynamicQuestions(), getDynamicSections()]);
        setQuestions(qs);
        setSections(secs);

        if (initialData) {
          setFormData({ ...initialData });
        } else {
          const nextSerial = await getNextSerialNo();
          setFormData({
            sectionA: { serialNo: nextSerial },
            ...secs.filter(s => s.isRepeater).reduce((acc, s) => ({ ...acc, [s.id]: [{ id: Date.now().toString() }] }), {})
          });
        }
      } catch (e) {
        console.error("Init failed", e);
      } finally {
        setInitLoading(false);
      }
    };
    init();
  }, [initialData]);

  const isVisible = (q: DynamicQuestion, data: Record<string, any>) => {
    if (!q.dependsOn) return true;
    const parentVal = data[q.dependsOn];
    if (q.visibleIf === undefined) return !!parentVal;
    if (typeof q.visibleIf === 'boolean') {
      return parentVal === q.visibleIf || parentVal === String(q.visibleIf);
    }
    if (Array.isArray(q.visibleIf)) {
      return q.visibleIf.includes(parentVal);
    }
    return parentVal === q.visibleIf;
  };

  const renderQuestions = (sectionId: string, data: Record<string, any>, updater: (k: string, v: any) => void) => {
    return questions
      .filter(q => q.section === sectionId)
      .filter(q => isVisible(q, data))
      .map(q => (
        <QuestionCard key={q.id} label={q.label} required={true}>
          {q.id === 'serialNo' ? (
             <input className={`${InputClass} bg-gray-100 text-gray-500`} value={data[q.id] || ''} readOnly />
          ) : q.type === 'yesno' ? (
            <YesNoInput value={data[q.id]} onChange={v => updater(q.id, v)} />
          ) : q.type === 'image' ? (
            <ImageInput value={data[q.id]} onChange={v => updater(q.id, v)} onProcessingChange={setIsMediaProcessing} />
          ) : q.type === 'date' ? (
            <input type="date" className={InputClass} value={data[q.id] || ''} onChange={e => updater(q.id, e.target.value)} required />
          ) : q.type === 'dropdown' ? (
            <select className={InputClass} value={data[q.id] || ''} onChange={e => updater(q.id, e.target.value)} required>
              <option value="">Select Option</option>
              {q.options?.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : q.type === 'multiselect' ? (
             <MultiSelect options={q.options || []} value={data[q.id]} onChange={v => updater(q.id, v)} />
          ) : q.type === 'textarea' ? (
             <textarea className={InputClass} rows={3} value={data[q.id] || ''} onChange={e => updater(q.id, e.target.value)} required />
          ) : (
             <input 
               type={q.id === 'phone' ? 'text' : q.type} 
               className={InputClass} 
               value={data[q.id] || ''} 
               onChange={e => {
                   if (q.id === 'phone') {
                       updater(q.id, e.target.value);
                   } else {
                       updater(q.id, q.type === 'number' && e.target.value !== '' ? parseFloat(e.target.value) : e.target.value);
                   }
               }} 
               required
             />
          )}
        </QuestionCard>
      ));
  };

  const validateCurrentSection = () => {
    const currentSection = sections[step];
    if (!currentSection) return true;

    // Filter questions that belong to this section AND are currently visible
    const currentQs = questions.filter(q => q.section === currentSection.id && isVisible(q, formData[currentSection.id] || {}));
    
    // Check Repeater Sections
    if (currentSection.isRepeater) {
       const items = formData[currentSection.id] || [];
       if (!Array.isArray(items) || items.length === 0) {
          addToast(`Please add at least one entry`, 'error');
          return false;
       }
       for(let i=0; i<items.length; i++) {
          for(const q of currentQs) {
             const val = items[i][q.id];
             if(val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0)) {
                // Allow explicit false/0 values, check for empty strings/arrays/nulls
                addToast(`Missing "${q.label}" in Entry ${i+1}`, 'error');
                return false;
             }
          }
       }
    } else {
       // Normal Sections
       const data = formData[currentSection.id] || {};
       for(const q of currentQs) {
          const val = data[q.id];
          if(q.id === 'serialNo') continue; // Skip read-only
          
          if(val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0)) {
             addToast(`Please fill "${q.label}"`, 'error');
             return false;
          }
       }
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isMediaProcessing) return;
    
    if (!validateCurrentSection()) return;
    
    setLoading(true);
    const surveyData: SurveyData = {
      id: initialData ? initialData.id : Date.now().toString(),
      submittedBy: user.id,
      submittedAt: new Date().toISOString(),
      ...formData
    };
    
    try {
      await saveSurvey(surveyData);
      addToast('Survey saved successfully!', 'success');
      onSuccess();
    } catch (e: any) {
      console.error(e);
      addToast('Failed to save survey. Check connection.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (isMediaProcessing) {
      addToast("Processing image...", "info");
      return;
    }
    
    if (!validateCurrentSection()) return;

    setStep(s => s + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const prevStep = () => {
    setStep(s => s - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateSectionData = (sectionId: string, key: string, val: any) => {
    const rawData = formData[sectionId];
    const currentSectionData = (rawData && typeof rawData === 'object' && !Array.isArray(rawData)) ? rawData : {};
    setFormData({ ...formData, [sectionId]: { ...currentSectionData, [key]: val } });
  };

  const updateRepeaterItem = (sectionId: string, idx: number, key: string, val: any) => {
    const list = formData[sectionId];
    const items = Array.isArray(list) ? [...list] : [];
    const rawItem = items[idx];
    const currentItem = (rawItem && typeof rawItem === 'object') ? rawItem : {};
    items[idx] = { ...currentItem, [key]: val };
    setFormData({ ...formData, [sectionId]: items });
  };
  
  const addRepeaterItem = (sectionId: string) => {
    const items = [...(formData[sectionId] || [])];
    items.push({ id: Date.now().toString() });
    setFormData({ ...formData, [sectionId]: items });
  };

  const removeRepeaterItem = (sectionId: string, idx: number) => {
    const items = [...(formData[sectionId] || [])];
    setFormData({ ...formData, [sectionId]: items.filter((_, i) => i !== idx) });
  };

  if (initLoading) return <div className="flex h-64 items-center justify-center"><Loader className="animate-spin text-emerald-500" size={32} /></div>

  const currentSection = sections[step];
  // step is 0-indexed. 
  // If we have 2 sections, indices are 0 and 1.
  // Last step is index 1.
  const isLastStep = step === sections.length - 1;

  return (
    <div className="w-full">
      {/* Simple Step Header */}
      <div className="mb-6">
         <h2 className="text-xl font-bold text-gray-800 mb-2">
            {currentSection?.label}
         </h2>
         <div className="w-full bg-gray-200 h-1.5 rounded-full overflow-hidden">
            <div 
              className="bg-emerald-500 h-full transition-all duration-300 ease-out" 
              style={{ width: `${((step + 1) / sections.length) * 100}%` }}
            />
         </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
         {currentSection && (
            <>
              {currentSection.isRepeater ? (
                <div className="space-y-6">
                    {(formData[currentSection.id] || []).map((item: any, idx: number) => (
                      <div key={item.id || idx} className="bg-gray-50 border border-gray-200 rounded-lg p-5 relative">
                        <div className="flex justify-between mb-4 border-b border-gray-200 pb-2">
                            <h4 className="font-bold text-gray-700 flex items-center text-sm uppercase tracking-wide"><UserIcon size={16} className="mr-2"/> Entry {idx+1}</h4>
                            <button type="button" onClick={() => removeRepeaterItem(currentSection.id, idx)} className="text-red-400 hover:text-red-600"><Trash2 size={16}/></button>
                        </div>
                        {renderQuestions(currentSection.id, item, (k, v) => updateRepeaterItem(currentSection.id, idx, k, v))}
                      </div>
                    ))}
                    <button type="button" onClick={() => addRepeaterItem(currentSection.id)} className="w-full py-3 border border-dashed border-emerald-300 text-emerald-600 rounded-lg font-bold flex justify-center items-center hover:bg-emerald-50 text-sm">
                      <Plus size={16} className="mr-2"/> Add Another Person
                    </button>
                </div>
              ) : (
                renderQuestions(currentSection.id, formData[currentSection.id] || {}, (k, v) => updateSectionData(currentSection.id, k, v))
              )}
            </>
         )}

         {/* Refreshed Bottom Bar - Floating Look */}
         <div className="fixed bottom-0 left-0 right-0 p-4 z-40 flex justify-center pointer-events-none">
            <div className="bg-white shadow-xl border border-gray-200 rounded-full px-4 py-2 flex items-center justify-between gap-4 pointer-events-auto max-w-sm w-full">
               {step > 0 ? (
                  <button type="button" onClick={prevStep} className="text-gray-500 hover:text-gray-800 font-medium text-xs flex items-center">
                    <ArrowLeft size={14} className="mr-1"/> Back
                  </button>
               ) : <div className="w-10"></div>} {/* Spacer */}
               
               {!isLastStep ? (
                  <button 
                    type="button" 
                    onClick={nextStep} 
                    disabled={isMediaProcessing}
                    className={`bg-emerald-600 text-white px-4 py-1.5 rounded-full font-bold shadow-md hover:bg-emerald-700 flex items-center text-sm transition-transform active:scale-95 ${isMediaProcessing ? 'opacity-50' : ''}`}
                  >
                    {isMediaProcessing ? 'Wait...' : 'Next'} 
                    {!isMediaProcessing && <ArrowRight size={14} className="ml-1"/>}
                  </button>
               ) : (
                  <button 
                    type="submit" 
                    disabled={loading || isMediaProcessing} 
                    className={`bg-emerald-600 text-white px-6 py-1.5 rounded-full font-bold shadow-md hover:bg-emerald-700 flex items-center text-sm transition-transform active:scale-95 disabled:bg-gray-400`}
                  >
                    {loading ? <Loader className="animate-spin mr-1" size={14}/> : <Save size={14} className="mr-1"/>} 
                    Submit
                  </button>
               )}
            </div>
         </div>
         
         {/* Spacer for fixed bottom bar */}
         <div className="h-24"></div>
      </form>
    </div>
  );
};

export default SurveyForm;